import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';

const TOOLS = [
  { name: 'Paper Writer',         icon: 'ri-file-text-line',   color: 'from-violet-500 to-violet-700',  path: '/paper-writer',         tag: 'Academic'     },
  { name: 'Genius Math',          icon: 'ri-calculator-line',  color: 'from-emerald-500 to-teal-600',   path: '/genius-math',          tag: 'STEM'         },
  { name: 'Digital AI Search',    icon: 'ri-search-eye-line',  color: 'from-cyan-500 to-sky-600',       path: '/digital-ai-search',    tag: 'Research'     },
  { name: 'Resume Builder',       icon: 'ri-profile-line',     color: 'from-orange-500 to-amber-600',   path: '/resume-builder',       tag: 'Career'       },
  { name: 'Business Plan Writer', icon: 'ri-briefcase-line',   color: 'from-rose-500 to-pink-600',      path: '/business-plan-writer', tag: 'Business'     },
  { name: 'eBook Creator',        icon: 'ri-book-open-line',   color: 'from-indigo-500 to-violet-600',  path: '/ebook-creator',        tag: 'Publishing'   },
  { name: 'Presentation Creator', icon: 'ri-slideshow-line',   color: 'from-fuchsia-500 to-pink-600',   path: '/presentation-creator', tag: 'Design'       },
  { name: 'Website Builder',      icon: 'ri-global-line',      color: 'from-teal-500 to-emerald-600',   path: '/website-builder',      tag: 'Web'          },
  { name: 'AI Image Generator',   icon: 'ri-image-line',       color: 'from-amber-500 to-orange-600',   path: '/ai-image-generator',   tag: 'Creative'     },
  { name: 'Master Architect',     icon: 'ri-building-2-line',  color: 'from-slate-600 to-gray-700',     path: '/master-architect',     tag: 'Architecture' },
  { name: 'AI Video',             icon: 'ri-video-line',       color: 'from-red-500 to-rose-600',       path: '/ai-video',             tag: 'Video'        },
];

export default function AdminQuickLaunch() {
  const { isAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── All hooks declared unconditionally ───────────────────────────────────
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hovered, setHovered] = useState<string | null>(null);
  const [justNavigated, setJustNavigated] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setSearch('');
    setTimeout(() => searchRef.current?.focus(), 80);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current && !panelRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, handleClose]);

  // Keyboard shortcuts: Escape to close, Ctrl+Shift+L to toggle
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { handleClose(); return; }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault();
        setOpen(prev => !prev);
        if (!open) setTimeout(() => searchRef.current?.focus(), 80);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, handleClose]);

  // ── Guard: only render for admin (after all hooks) ───────────────────────
  if (!isAdmin) return null;

  const currentPath = location.pathname;

  const filtered = search.trim()
    ? TOOLS.filter(
        (t) =>
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.tag.toLowerCase().includes(search.toLowerCase())
      )
    : TOOLS;

  const handleNavigate = (path: string, name: string) => {
    setJustNavigated(name);
    handleClose();
    setTimeout(() => {
      navigate(path);
      setJustNavigated(null);
    }, 180);
  };

  return (
    <>
      {/* ── Floating trigger button ── */}
      <button
        ref={btnRef}
        onClick={() => (open ? handleClose() : handleOpen())}
        title="Admin Quick Launch (Ctrl+Shift+L)"
        className={`fixed bottom-6 right-6 z-[9990] flex items-center gap-2.5 px-4 py-3 rounded-2xl shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap select-none
          ${open
            ? 'bg-gray-900 text-white scale-95'
            : 'bg-gradient-to-r from-amber-500 to-yellow-400 text-white hover:scale-105'
          }`}
        style={{ boxShadow: open ? '0 8px 32px rgba(0,0,0,0.35)' : '0 8px 32px rgba(245,158,11,0.45)' }}
      >
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <i className={`text-base transition-transform duration-300 ${open ? 'ri-close-line' : 'ri-vip-crown-2-fill'}`}></i>
        </div>
        <span className="text-sm font-extrabold tracking-tight">
          {open ? 'Close' : 'Quick Launch'}
        </span>
        {/* Ambient pulse ring */}
        {!open && (
          <span
            className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ animation: 'qlPing 2.4s cubic-bezier(0,0,0.2,1) infinite', background: 'rgba(251,191,36,0.3)' }}
          ></span>
        )}
      </button>

      {/* ── Slide-up panel ── */}
      {open && (
        <div
          ref={panelRef}
          className="fixed bottom-[72px] right-6 z-[9989] w-[380px] rounded-3xl overflow-hidden bg-white"
          style={{
            boxShadow: '0 24px 64px rgba(0,0,0,0.28)',
            animation: 'qlSlideUp 0.22s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        >
          {/* ── Header ── */}
          <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-400 px-5 py-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 flex-shrink-0">
                  <i className="ri-shield-star-fill text-white text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-extrabold leading-tight">Admin Quick Launch</p>
                  <p className="text-white/70 text-xs font-medium">Jump to any tool instantly</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/20 border border-white/30">
                <i className="ri-apps-2-line text-white text-xs"></i>
                <span className="text-white text-xs font-bold">{TOOLS.length} tools</span>
              </div>
            </div>

            {/* Search bar */}
            <div className="flex items-center gap-2.5 bg-white/20 border border-white/30 rounded-xl px-3 py-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-line text-white/80 text-sm"></i>
              </div>
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search tools…"
                className="flex-1 bg-transparent text-white text-sm font-medium placeholder-white/55 outline-none"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="w-4 h-4 flex items-center justify-center cursor-pointer flex-shrink-0"
                >
                  <i className="ri-close-line text-white/70 text-xs hover:text-white transition-colors"></i>
                </button>
              )}
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-white/70 text-xs font-bold leading-none">⌘</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-white/70 text-xs font-bold leading-none">⇧</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-white/20 text-white/70 text-xs font-bold leading-none">L</kbd>
              </div>
            </div>
          </div>

          {/* ── Tool list ── */}
          <div className="max-h-[400px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-3">
                <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-100">
                  <i className="ri-search-line text-gray-400 text-xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-500">No tools match &ldquo;{search}&rdquo;</p>
                <button
                  onClick={() => setSearch('')}
                  className="text-xs font-bold text-amber-600 hover:text-amber-700 transition-colors cursor-pointer"
                >
                  Clear search
                </button>
              </div>
            ) : (
              <div className="p-3 flex flex-col gap-0.5">
                {filtered.map((tool) => {
                  const isActive = currentPath === tool.path || currentPath.startsWith(tool.path + '/');
                  const isJustNav = justNavigated === tool.name;
                  const isHov = hovered === tool.name;
                  return (
                    <button
                      key={tool.name}
                      onClick={() => handleNavigate(tool.path, tool.name)}
                      onMouseEnter={() => setHovered(tool.name)}
                      onMouseLeave={() => setHovered(null)}
                      className={`w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-left transition-all duration-150 cursor-pointer
                        ${isActive
                          ? 'bg-amber-50 border border-amber-200'
                          : isHov
                          ? 'bg-gray-50 border border-gray-100'
                          : 'border border-transparent hover:bg-gray-50'
                        }`}
                    >
                      {/* Icon */}
                      <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${tool.color} flex-shrink-0 transition-transform duration-150 ${isHov ? 'scale-110' : ''}`}>
                        <i className={`${tool.icon} text-white text-sm`}></i>
                      </div>

                      {/* Name + tag */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-bold truncate ${isActive ? 'text-amber-700' : 'text-gray-900'}`}>
                            {tool.name}
                          </p>
                          {isActive && (
                            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold whitespace-nowrap flex-shrink-0">
                              <i className="ri-map-pin-2-fill text-xs"></i>
                              Here
                            </span>
                          )}
                        </div>
                        <p className={`text-xs font-medium ${isActive ? 'text-amber-500' : 'text-gray-400'}`}>
                          {tool.tag}
                        </p>
                      </div>

                      {/* Arrow / check / pin */}
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                        isJustNav ? 'text-emerald-500' :
                        isActive  ? 'text-amber-400' :
                        isHov     ? 'text-gray-600 translate-x-0.5' :
                                    'text-gray-300'
                      }`}>
                        <i className={`text-sm ${isJustNav ? 'ri-check-line' : isActive ? 'ri-map-pin-2-line' : 'ri-arrow-right-s-line'}`}></i>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/70 flex items-center justify-between">
            <button
              onClick={() => { handleClose(); navigate('/'); }}
              className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-home-4-line text-sm"></i>
              Back to Home
            </button>
            <p className="text-xs text-gray-400 font-medium">
              {filtered.length} / {TOOLS.length} tools
            </p>
          </div>
        </div>
      )}

      <style>{`
        @keyframes qlSlideUp {
          from { opacity: 0; transform: translateY(14px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes qlPing {
          0%   { transform: scale(1);    opacity: 0.55; }
          70%  { transform: scale(1.45); opacity: 0;    }
          100% { transform: scale(1.45); opacity: 0;    }
        }
      `}</style>
    </>
  );
}
